import apiClient from './apiClient';
import { withActingAs } from './actingAs';
import type { ApiResponse } from '../types';

const API_TYPE = 'api' as const;

/**
 * Terminate scopes mirror the backend ManualExitScope. ACCOUNT (one broker
 * account by broker+client_id) and ALL are terminate-only wide scopes; exit
 * does not expose them.
 */
export type TerminateScope =
  | 'SINGLE' | 'MULTIPLE' | 'BASKET' | 'STRATEGY' | 'ACCOUNT' | 'ALL';

export interface TerminateRequest {
  scope: TerminateScope;
  position_ids?: string[];
  basket_id?: string;
  strategy_id?: string;
  broker?: string;       // ACCOUNT scope
  client_id?: string;    // ACCOUNT scope
  reason?: string;       // optional free-text audit note
  dry_run?: boolean;     // true → return the count, write nothing
}

export interface TerminateResponse {
  terminated_count: number;
  skipped_version_conflicts?: number;
  failed?: number;
  chains_disarmed?: number;
  /** dry_run only — count of armed chains a real terminate would disarm. */
  chains_to_disarm?: number;
  position_ids: string[];
}

/** Preview shape returned by a dry_run terminate. */
export interface TerminatePreview {
  terminated_count: number;
  chains_to_disarm: number;
  position_ids: string[];
}

/**
 * Terminate NEVER calls the broker — it flips positions to
 * position_status=TERMINATED so the engine stops managing them, leaving any
 * live broker position/order in place. One-way. `?acting_as=<id>` is appended
 * when acting on behalf; the backend resolves it in allow_admin=False mode
 * (SuperAdmin / AM-with-relation may write on behalf; Admin gets 403).
 */
function terminateUrl(): string {
  const qs = withActingAs(new URLSearchParams()).toString();
  return qs ? `/positions/terminate?${qs}` : '/positions/terminate';
}

class TerminateService {
  async terminatePositions(request: TerminateRequest): Promise<TerminateResponse> {
    try {
      const response = await apiClient.post<ApiResponse<TerminateResponse>>(
        terminateUrl(),
        request,
        undefined,
        API_TYPE
      );
      const wrapped = response as ApiResponse<TerminateResponse>;
      if (!wrapped.success || !wrapped.data) {
        throw new Error(wrapped.message || 'Failed to terminate positions');
      }
      return wrapped.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to terminate positions'
      );
    }
  }

  /**
   * Dry-run: fetch the authoritative in-scope count from the backend without
   * writing. Used by ConfirmTerminateDialog because the date-scoped
   * schedule/positions view would under-count positional / other-account rows
   * for the wide (STRATEGY/BASKET/ACCOUNT/ALL) scopes.
   */
  async previewTerminate(request: TerminateRequest): Promise<TerminatePreview> {
    const data = await this.terminatePositions({ ...request, dry_run: true });
    return {
      terminated_count: data.terminated_count,
      chains_to_disarm: data.chains_to_disarm ?? 0,
      position_ids: data.position_ids ?? [],
    };
  }

  terminateSingle(positionId: string, reason?: string): Promise<TerminateResponse> {
    return this.terminatePositions({ scope: 'SINGLE', position_ids: [positionId], reason });
  }

  terminateStrategy(strategyId: string, reason?: string): Promise<TerminateResponse> {
    return this.terminatePositions({ scope: 'STRATEGY', strategy_id: strategyId, reason });
  }

  terminateBasket(basketId: string, reason?: string): Promise<TerminateResponse> {
    return this.terminatePositions({ scope: 'BASKET', basket_id: basketId, reason });
  }

  terminateAccount(broker: string, clientId: string, reason?: string): Promise<TerminateResponse> {
    return this.terminatePositions({ scope: 'ACCOUNT', broker, client_id: clientId, reason });
  }

  terminateAll(reason?: string): Promise<TerminateResponse> {
    return this.terminatePositions({ scope: 'ALL', reason });
  }
}

const terminateService = new TerminateService();
export default terminateService;
